import { useState, useEffect, useRef } from 'react'
import { adminAPI } from '@utils/api'
import AdminLayout from '@components/admin/AdminLayout'
import {
  User, Lock, Camera, Save, Loader2, Eye, EyeOff,
  Phone, Building2, Shield, CheckCircle2, AlertCircle,
  Mail, AtSign, KeyRound
} from 'lucide-react'
import toast from 'react-hot-toast'
import { format } from 'date-fns'

const TABS = [
  { id: 'profile',  label: 'Profile',          icon: User },
  { id: 'password', label: 'Change Password',   icon: Lock },
]

const DEPARTMENTS = ['Management', 'Sales', 'Operations', 'Technical', 'Support']

const fi = 'w-full px-3.5 py-2.5 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-gray-750 transition-all'

const PasswordInput = ({ label, value, onChange, placeholder, icon: Icon }) => {
  const [show, setShow] = useState(false)
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />}{label}
      </label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={fi + ' pr-10'}
        />
        <button type="button" onClick={() => setShow(v => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  )
}

const PasswordStrength = ({ pw }) => {
  const checks = [
    { label: '8+ chars',         ok: pw.length >= 8 },
    { label: 'Uppercase',        ok: /[A-Z]/.test(pw) },
    { label: 'Number',           ok: /\d/.test(pw) },
    { label: 'Special char',     ok: /[^A-Za-z0-9]/.test(pw) },
  ]
  const score = checks.filter(c => c.ok).length
  const color = score <= 1 ? 'bg-red-500' : score === 2 ? 'bg-yellow-500' : score === 3 ? 'bg-blue-500' : 'bg-green-500'
  if (!pw) return null
  return (
    <div className="space-y-2">
      <div className="flex gap-1">
        {[1,2,3,4].map(i => (
          <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= score ? color : 'bg-gray-200 dark:bg-gray-700'}`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {checks.map(c => (
          <span key={c.label} className={`text-[11px] flex items-center gap-1 ${c.ok ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
            <span>{c.ok ? '✓' : '○'}</span> {c.label}
          </span>
        ))}
      </div>
    </div>
  )
}

const ConfirmPasswordModal = ({ onConfirm, onClose, loading }) => {
  const [pw, setPw] = useState('')
  const [show, setShow] = useState(false)
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 w-full max-w-sm p-6 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white text-sm">Verify Current Password</h3>
            <p className="text-xs text-gray-500 mt-0.5">Enter your current password to confirm this change</p>
          </div>
        </div>
        <div className="relative">
          <input
            autoFocus
            type={show ? 'text' : 'password'}
            value={pw}
            onChange={e => setPw(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && pw && onConfirm(pw)}
            placeholder="Current password"
            className={fi + ' pr-10'}
          />
          <button type="button" onClick={() => setShow(v => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <div className="flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            Cancel
          </button>
          <button onClick={() => onConfirm(pw)} disabled={!pw || loading}
            className="flex-1 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-colors">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Confirm
          </button>
        </div>
      </div>
    </div>
  )
}

const AdminProfile = () => {
  const [tab,      setTab]      = useState('profile')
  const [admin,    setAdmin]    = useState(null)
  const [loading,  setLoading]  = useState(true)
  const [saving,   setSaving]   = useState(false)
  const [avatarLoading, setAvatarLoading] = useState(false)
  const avatarRef = useRef(null)

  const [form, setForm] = useState({ name: '', phone: '', whatsapp: '', department: '' })

  const [pwForm, setPwForm]     = useState({ newPassword: '', confirmPassword: '' })
  const [showConfirm, setShowConfirm] = useState(false)
  const [confirmLoading, setConfirmLoading] = useState(false)

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  useEffect(() => {
    adminAPI.me().then(res => {
      const a = res?.data?.admin
      if (a) {
        setAdmin(a)
        setForm({ name: a.name || '', phone: a.phone || '', whatsapp: a.whatsapp || '', department: a.department || 'Management' })
      }
    }).catch(() => toast.error('Failed to load profile'))
    .finally(() => setLoading(false))
  }, [])

  const saveProfile = async () => {
    if (!form.name.trim()) return toast.error('Name is required')
    setSaving(true)
    try {
      const res = await adminAPI.updateProfile(form)
      const a = res?.data?.admin
      if (a) { setAdmin(a); localStorage.setItem('adminUser', JSON.stringify(a)) }
      toast.success('Profile updated')
    } catch (e) { toast.error(e?.message || 'Update failed') }
    finally { setSaving(false) }
  }

  const handleAvatarChange = async (file) => {
    if (!file) return
    setAvatarLoading(true)
    try {
      const res = await adminAPI.uploadAvatar(file)
      const a = res?.data?.admin
      if (a) { setAdmin(a); localStorage.setItem('adminUser', JSON.stringify(a)) }
      toast.success('Avatar updated')
    } catch (e) { toast.error(e?.message || 'Avatar upload failed') }
    finally { setAvatarLoading(false) }
  }

  const submitPasswordChange = async (currentPassword) => {
    setConfirmLoading(true)
    try {
      await adminAPI.changePassword({ currentPassword, newPassword: pwForm.newPassword, confirmPassword: pwForm.confirmPassword })
      toast.success('Password changed successfully')
      setPwForm({ newPassword: '', confirmPassword: '' })
      setShowConfirm(false)
    } catch (e) { toast.error(e?.message || 'Password change failed') }
    finally { setConfirmLoading(false) }
  }

  const handlePasswordSubmit = () => {
    if (!pwForm.newPassword || !pwForm.confirmPassword) return toast.error('Fill in all password fields')
    if (pwForm.newPassword.length < 8) return toast.error('Password must be at least 8 characters')
    if (pwForm.newPassword !== pwForm.confirmPassword) return toast.error('Passwords do not match')
    setShowConfirm(true)
  }

  const initials = admin?.name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?'
  const roleBadge = { super_admin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', admin: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', manager: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', counselor: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' }

  return (
    <AdminLayout>
      {showConfirm && (
        <ConfirmPasswordModal
          loading={confirmLoading}
          onClose={() => setShowConfirm(false)}
          onConfirm={submitPasswordChange}
        />
      )}

      <div className="space-y-5">
        {/* Header */}
        <div>
          <h1 className="text-xl font-extrabold text-gray-900 dark:text-white">My Profile</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your account details and password</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-7 h-7 animate-spin text-primary-500" />
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-5">

            {/* ── Left: avatar card ── */}
            <div className="lg:col-span-1 space-y-4">
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 flex flex-col items-center gap-4 text-center">
                <div className="relative group">
                  <div className="w-24 h-24 rounded-2xl overflow-hidden bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white text-2xl font-bold ring-4 ring-white dark:ring-gray-900 shadow-lg">
                    {admin?.avatar
                      ? <img src={`${admin.avatar}?t=${Date.now()}`} alt={admin.name} className="w-full h-full object-cover" />
                      : initials
                    }
                  </div>
                  <button onClick={() => avatarRef.current?.click()} disabled={avatarLoading}
                    className="absolute inset-0 rounded-2xl bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    {avatarLoading
                      ? <Loader2 className="w-5 h-5 text-white animate-spin" />
                      : <Camera className="w-5 h-5 text-white" />
                    }
                  </button>
                  <input ref={avatarRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden"
                    onChange={e => { if (e.target.files[0]) handleAvatarChange(e.target.files[0]); e.target.value = '' }} />
                </div>

                <div>
                  <h2 className="font-bold text-gray-900 dark:text-white">{admin?.name}</h2>
                  <p className="text-xs text-gray-500 mt-0.5">{admin?.email}</p>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${roleBadge[admin?.role] || ''}`}>
                      {admin?.role?.replace('_', ' ')}
                    </span>
                  </div>
                </div>

                <div className="w-full border-t border-gray-100 dark:border-gray-800 pt-4 space-y-2.5 text-left">
                  <div className="flex items-center gap-2.5 text-sm text-gray-600 dark:text-gray-400">
                    <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span className="truncate">{admin?.email}</span>
                  </div>
                  {admin?.phone && (
                    <div className="flex items-center gap-2.5 text-sm text-gray-600 dark:text-gray-400">
                      <Phone className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <span>{admin.phone}</span>
                    </div>
                  )}
                  {admin?.department && (
                    <div className="flex items-center gap-2.5 text-sm text-gray-600 dark:text-gray-400">
                      <Building2 className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <span>{admin.department}</span>
                    </div>
                  )}
                  {admin?.lastLogin && (
                    <div className="flex items-center gap-2.5 text-xs text-gray-400">
                      <Shield className="w-3.5 h-3.5 shrink-0" />
                      <span>Last login {format(new Date(admin.lastLogin), 'dd MMM yyyy, h:mm a')}</span>
                    </div>
                  )}
                </div>

                <button onClick={() => avatarRef.current?.click()} disabled={avatarLoading}
                  className="w-full py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center justify-center gap-2 transition-colors">
                  <Camera className="w-3.5 h-3.5" /> Change Avatar
                </button>
              </div>
            </div>

            {/* ── Right: tabs ── */}
            <div className="lg:col-span-2">
              {/* Tab bar */}
              <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl mb-5">
                {TABS.map(t => {
                  const Icon = t.icon
                  return (
                    <button key={t.id} onClick={() => setTab(t.id)}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-semibold transition-all
                        ${tab === t.id
                          ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                        }`}>
                      <Icon className="w-4 h-4" />{t.label}
                    </button>
                  )
                })}
              </div>

              {/* Profile tab */}
              {tab === 'profile' && (
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                  <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 dark:border-gray-800">
                    <div className="w-7 h-7 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                      <User className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <h3 className="font-bold text-gray-900 dark:text-white text-sm">Personal Information</h3>
                  </div>

                  <div className="p-6 grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Full Name *</label>
                      <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Your full name" className={fi} />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                        <Mail className="w-3 h-3" /> Email
                      </label>
                      <input value={admin?.email || ''} disabled
                        className={fi + ' opacity-60 cursor-not-allowed'} />
                      <p className="text-[10px] text-gray-400">Email cannot be changed</p>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                        <Phone className="w-3 h-3" /> Phone
                      </label>
                      <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+91 98765 43210" className={fi} />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                        <AtSign className="w-3 h-3" /> WhatsApp
                      </label>
                      <input value={form.whatsapp} onChange={e => set('whatsapp', e.target.value)} placeholder="919876543210" className={fi} />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                        <Building2 className="w-3 h-3" /> Department
                      </label>
                      <select value={form.department} onChange={e => set('department', e.target.value)} className={fi}>
                        {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                        <Shield className="w-3 h-3" /> Role
                      </label>
                      <input value={admin?.role?.replace('_', ' ') || ''} disabled
                        className={fi + ' opacity-60 cursor-not-allowed capitalize'} />
                      <p className="text-[10px] text-gray-400">Role is managed by a super admin</p>
                    </div>
                  </div>

                  <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex justify-end">
                    <button onClick={saveProfile} disabled={saving}
                      className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold disabled:opacity-60 transition-colors">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save Changes
                    </button>
                  </div>
                </div>
              )}

              {/* Password tab */}
              {tab === 'password' && (
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                  <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 dark:border-gray-800">
                    <div className="w-7 h-7 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                      <Lock className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 dark:text-white text-sm">Change Password</h3>
                      <p className="text-xs text-gray-500">You'll be asked to verify your current password before saving</p>
                    </div>
                  </div>

                  <div className="p-6 space-y-4 max-w-md">
                    <div className="p-3.5 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 flex items-start gap-2.5">
                      <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-700 dark:text-amber-400">After changing your password, you'll remain logged in on this device.</p>
                    </div>

                    <PasswordInput
                      label="New Password"
                      icon={KeyRound}
                      value={pwForm.newPassword}
                      onChange={e => setPwForm(p => ({ ...p, newPassword: e.target.value }))}
                      placeholder="Min 8 characters"
                    />

                    <PasswordStrength pw={pwForm.newPassword} />

                    <PasswordInput
                      label="Confirm New Password"
                      icon={CheckCircle2}
                      value={pwForm.confirmPassword}
                      onChange={e => setPwForm(p => ({ ...p, confirmPassword: e.target.value }))}
                      placeholder="Repeat new password"
                    />

                    {pwForm.confirmPassword && pwForm.newPassword !== pwForm.confirmPassword && (
                      <p className="text-xs text-red-500 flex items-center gap-1.5">
                        <AlertCircle className="w-3.5 h-3.5" /> Passwords do not match
                      </p>
                    )}
                    {pwForm.confirmPassword && pwForm.newPassword === pwForm.confirmPassword && (
                      <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Passwords match
                      </p>
                    )}
                  </div>

                  <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex justify-end">
                    <button onClick={handlePasswordSubmit}
                      className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold transition-colors">
                      <Lock className="w-4 h-4" /> Change Password
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

export default AdminProfile
